/**
 * ThemeProvider — Fetches theme configuration from the public CMS API
 * and applies CSS custom properties to the document root.
 */
import { useEffect, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface ThemeConfig {
  primary_color: string
  secondary_color: string
  accent_color: string
  text_color: string
  background_color: string
  header_background_color: string
  header_text_color: string
  footer_background_color: string
  footer_text_color: string
  font_family: string
  border_radius?: 'small' | 'medium' | 'large'
  button_style?: 'solid' | 'outline' | 'ghost'
  enable_animations?: boolean
  animation_speed?: 'slow' | 'normal' | 'fast'
}

export const DEFAULT_THEME: ThemeConfig = {
  primary_color: '#3d868b',
  secondary_color: '#10B981',
  accent_color: '#cc5840',
  text_color: '#1F2937',
  background_color: '#FFFFFF',
  header_background_color: '#FFFFFF',
  header_text_color: '#111827',
  footer_background_color: '#111827',
  footer_text_color: '#D1D5DB',
  font_family: 'Outfit',
  border_radius: 'medium',
  button_style: 'solid',
  enable_animations: true,
  animation_speed: 'normal',
}

export function useTheme() {
  return useQuery<ThemeConfig>({
    queryKey: ['cms-theme'],
    queryFn: async () => {
      const { data } = await api.get<{ data: ThemeConfig }>('/cms/theme')
      return data.data
    },
    staleTime: 1000 * 60,
    retry: 1,
  })
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const fullHex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 61, g: 134, b: 139 }
}

function mix(
  color: { r: number; g: number; b: number },
  mixColor: { r: number; g: number; b: number },
  weight: number,
) {
  return {
    r: Math.round(color.r * (1 - weight) + mixColor.r * weight),
    g: Math.round(color.g * (1 - weight) + mixColor.g * weight),
    b: Math.round(color.b * (1 - weight) + mixColor.b * weight),
  }
}

function hexToHslChannels(hex: string): string {
  const { r, g, b } = hexToRgb(hex)
  const rNorm = r / 255
  const gNorm = g / 255
  const bNorm = b / 255
  const max = Math.max(rNorm, gNorm, bNorm)
  const min = Math.min(rNorm, gNorm, bNorm)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6
        break
      case gNorm:
        h = ((bNorm - rNorm) / d + 2) / 6
        break
      default:
        h = ((rNorm - gNorm) / d + 4) / 6
        break
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

function applyColorShades(
  root: CSSStyleDeclaration,
  prefix: string,
  hex: string,
) {
  const rgb = hexToRgb(hex)
  const white = { r: 255, g: 255, b: 255 }
  const black = { r: 0, g: 0, b: 0 }

  const shades = {
    50: mix(rgb, white, 0.92),
    100: mix(rgb, white, 0.8),
    200: mix(rgb, white, 0.6),
    300: mix(rgb, white, 0.4),
    400: mix(rgb, white, 0.2),
    500: rgb,
    600: mix(rgb, black, 0.15),
    700: mix(rgb, black, 0.3),
    800: mix(rgb, black, 0.45),
    900: mix(rgb, black, 0.6),
    950: mix(rgb, black, 0.75),
  }

  Object.entries(shades).forEach(([shade, color]) => {
    root.setProperty(`--${prefix}-${shade}`, `${color.r} ${color.g} ${color.b}`)
  })
}

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { data: theme } = useTheme()

  const config = { ...DEFAULT_THEME, ...theme }

  useEffect(() => {
    const root = document.documentElement.style

    root.setProperty('--color-primary', config.primary_color)
    root.setProperty('--color-secondary', config.secondary_color)
    root.setProperty('--color-accent', config.accent_color)
    root.setProperty('--color-text', config.text_color)
    root.setProperty('--color-bg', config.background_color)
    root.setProperty('--foreground', hexToHslChannels(config.text_color))
    root.setProperty('--background', hexToHslChannels(config.background_color))

    root.setProperty('--font-family', `'${config.font_family}', ui-sans-serif, system-ui, sans-serif`)

    const borderRadiusMap = {
      small: '4px',
      medium: '8px',
      large: '16px',
    }
    root.setProperty('--border-radius', borderRadiusMap[config.border_radius || 'medium'])
    root.setProperty('--button-style', config.button_style || 'solid')

    const speedMap = { slow: '500ms', normal: '300ms', fast: '150ms' }
    root.setProperty('--animation-speed', speedMap[config.animation_speed || 'normal'])

    applyColorShades(root, 'primary', config.primary_color)

    const setRgbVar = (name: string, hex: string) => {
      const { r, g, b } = hexToRgb(hex)
      root.setProperty(name, `${r} ${g} ${b}`)
    }

    setRgbVar('--header-bg', config.header_background_color)
    setRgbVar('--header-text', config.header_text_color)
    setRgbVar('--footer-bg', config.footer_background_color)
    setRgbVar('--footer-text', config.footer_text_color)

    const accentRgb = hexToRgb(config.accent_color)
    const accent600 = mix(accentRgb, { r: 0, g: 0, b: 0 }, 0.15)
    root.setProperty('--accent-500', `${accentRgb.r} ${accentRgb.g} ${accentRgb.b}`)
    root.setProperty('--accent-600', `${accent600.r} ${accent600.g} ${accent600.b}`)

    document.documentElement.dataset.buttonStyle = config.button_style || 'solid'
    document.documentElement.dataset.animations = config.enable_animations ? 'on' : 'off'
  }, [config])

  useEffect(() => {
    if (!config.font_family) return

    const fontId = 'dynamic-google-font'
    let linkElement = document.getElementById(fontId) as HTMLLinkElement | null
    const formattedFont = config.font_family.replace(/\s+/g, '+')
    const fontUrl = `https://fonts.googleapis.com/css2?family=${formattedFont}:wght@300;400;500;600;700;800&display=swap`

    if (!linkElement) {
      linkElement = document.createElement('link')
      linkElement.id = fontId
      linkElement.rel = 'stylesheet'
      document.head.appendChild(linkElement)
    }
    linkElement.href = fontUrl
  }, [config.font_family])

  return <>{children}</>
}
